import {
  approveTransactionReviewService,
  declineTransactionReviewService,
  getTransactionReviewDetailsService,
  getTransactionReviewListService,
} from '@/services/transactionReview';
import {
  ApproveDeclineTransactionReviewRequestBody,
  queryKeys,
  TransactionReviewListQueryParams,
} from '@/types/transactionReview';
import { convertObjectValuesToString } from '@/utils/responseConvertors';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetTransactionReviewList = (
  queryParams: TransactionReviewListQueryParams,
) => {
  const params = new URLSearchParams(
    convertObjectValuesToString(queryParams),
  ).toString();
  const { data, isFetching, refetch } = useQuery({
    queryKey: [queryKeys.list, queryParams],
    queryFn: () => getTransactionReviewListService(params),
  });

  return {
    transactionReviews: data?.records || [],
    totalRecordCount: data?.totalRecordCount || 0,
    isTransactionsReviewListPending: isFetching,
    refetch,
  };
};

export const useGetTransactionReviewDetails = (id: string) => {
  const { data, isPending, refetch } = useQuery({
    queryKey: [queryKeys.details, id],
    queryFn: () => getTransactionReviewDetailsService(id),
  });

  return {
    transactionReviewDetails: data,
    isTransactionReviewDetailsPending: isPending,
    refetch,
  };
};

export const useApproveDeclineTransactionReview = () => {
  const queryClient = useQueryClient();
  const {
    isPending: isLoadingApproveTransactionReview,
    mutateAsync: approveTransactionReview,
  } = useMutation({
    mutationFn: (data: ApproveDeclineTransactionReviewRequestBody) =>
      approveTransactionReviewService(data),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  const {
    isPending: isLoadingDeclineTransactionReview,
    mutateAsync: declineTransactionReview,
  } = useMutation({
    mutationFn: (data: ApproveDeclineTransactionReviewRequestBody) =>
      declineTransactionReviewService(data),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  return {
    isLoadingApproveDeclineTransactionReview:
      isLoadingApproveTransactionReview || isLoadingDeclineTransactionReview,
    approveTransactionReview,
    declineTransactionReview,
  };
};
